import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { permissionLevelEnum } from '../../../../constants';
import { formResponseStatusEnum } from '../../../../constants/enums/form';
import { useAuth } from '../../../../hooks/useAuth';
import { FormResponseWithSiteAndFilledBy } from '../../../../services/apiSLTP/qhse/forms/formResponses';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface FormResponsesTableProps {
    templateName?: string;
    templateId: string;
    onSelectResponse: (responseId: string) => void;
    responses?: FormResponseWithSiteAndFilledBy[];
    isResponsesLoading?: boolean;
    showSiteColumn?: boolean;
}

const FormResponsesTable: React.FC<FormResponsesTableProps> = ({
    templateName,
    templateId,
    onSelectResponse,
    responses,
    isResponsesLoading,
    showSiteColumn,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();

    const handleOpen = (form: FormResponseWithSiteAndFilledBy) => {
        onSelectResponse(form._id);
    };

    const columnDefs: ColDef<FormResponseWithSiteAndFilledBy>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            valueGetter: (params) => (params.data ? formatName(params.data.filledBy.firstName, params.data.filledBy.lastName) : ''),
            initialFlex: 1,
            minWidth: 300,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            colId: 'actions',
            sortable: false,
            filter: false,
            initialWidth: 250,
            cellRenderer: (params: ICellRendererParams<FormResponseWithSiteAndFilledBy>) => {
                if (!params.data) return null;
                const form = params.data;
                return (
                    <IconLine>
                        <IconButton iconName="visibility" title={capitalizeFirstLetter(t('open'))} onClick={() => handleOpen(form)} />
                    </IconLine>
                );
            },
        },

        ...((showSiteColumn
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('site')),
                      field: 'siteId.name',
                      colId: 'site',
                      initialWidth: 300,
                  },
              ]
            : []) as ColDef<FormResponseWithSiteAndFilledBy>[]),
        {
            headerName: capitalizeFirstLetter(t('date')),
            field: 'submittedAt',
            valueGetter: (params) => params.data?.submittedAt || '',
            valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
            colId: 'submittedAt',
        },

        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<FormResponseWithSiteAndFilledBy>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={
                    responses
                        ?.filter((response) => response.status === formResponseStatusEnum.SUBMITTED)
                        .sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || '')) || []
                }
                loading={isResponsesLoading}
                exportCsvFileName={`responses-${templateName}`}
                agGridProps={{ getRowId: ({ data }) => data._id, domLayout: 'autoHeight', rowNumbers: true }}
            />
        </>
    );
};

export default FormResponsesTable;
